import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './EmailVerification.css';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const email = searchParams.get('key') || searchParams.get('email');
  const token = searchParams.get('token');

  useEffect(() => {
    if (email && token) {
      verifyEmail();
    } else {
      setError('Invalid verification link. Missing email or token.');
      setLoading(false);
    }
  }, [email, token]);

  const verifyEmail = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const apiUrl = `${process.env.REACT_APP_API_URL}/api/v1/email-verifications?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`;
      console.log('API URL:', apiUrl);
      console.log('Email:', email);
      console.log('Token:', token);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log('Response status:', response.status);

      const data = await response.json();

      // Handle new response format
      if (data.status && data.data) {
        // Check if email was already verified
        if (data.data.verification_status === 'already_verified') {
          setVerificationStatus('already_verified');
          setSuccess(data.message || 'Email was already verified');
        } else {
          setVerificationStatus('success');
          setSuccess(data.message || 'Email verified. You can now log in');
        }
        
        // Store verification data in localStorage if needed
        localStorage.setItem('emailVerified', 'true');
        localStorage.setItem('verifiedEmail', data.data.email);
        
        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 5000);
      } else {
        // Handle error cases (status: false)
        setVerificationStatus('error');
        setError(data.message || 'Email verification failed. Please try again.');
      }
    } catch (err) {
      console.error('Email verification error:', err);
      setVerificationStatus('error');
      if (err.message.includes('404')) {
        setError('Verification service not found. Please contact support.');
      } else if (err.message.includes('500')) {
        setError('Server error. Please try again later.');
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email: email })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status) {
        setSuccess(data.message || 'Verification email sent successfully!');
      } else {
        setError(data.message || 'Failed to resend verification email.');
      }
    } catch (err) {
      console.error('Resend verification error:', err);
      if (err.message.includes('404')) {
        setError('Resend verification feature is not available. Please contact support.');
      } else {
        setError('Failed to resend verification email. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="email-verification-container">
        <div className="verification-card">
          <div className="loading-spinner"></div>
          <h2>Verifying Email...</h2>
          <p>Please wait while we verify your email address.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-verification-container">
      <div className="verification-card">
        {verificationStatus === 'success' || verificationStatus === 'already_verified' ? (
          <>
            <div className="success-icon">✅</div>
            <h2>{verificationStatus === 'already_verified' ? 'Email Already Verified!' : 'Email Verified Successfully!'}</h2>
            <p className="success-message">{success}</p>
            <p>You will be redirected to your dashboard shortly...</p>
            <div className="verification-details">
              <p><strong>Email:</strong> {email}</p>
              <p><strong>Status:</strong> Verified</p>
            </div>
            <button 
              className="btn-primary" 
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </button>
          </>
        ) : (
          <>
            <div className="error-icon">❌</div>
            <h2>Email Verification Failed</h2>
            <p className="error-message">{error}</p>
            <div className="verification-actions">
              <button 
                className="btn-secondary" 
                onClick={resendVerification}
                disabled={loading}
              >
                Resend Verification Email
              </button>
              <button 
                className="btn-primary" 
                onClick={() => navigate('/')}
              >
                Return to Home
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;
